import { FC, ReactElement, useCallback, useEffect, useRef } from 'react';

import { addBebyggelseobjektUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { setHoveredId } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';
import { clearUtkastState, postUtkast } from 'store/utkastSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useGetCurrentAktivitet } from 'hooks/useGetCurrentAktivitet';

import { RaaSpinner } from 'raa-react';

import { LmPopupRow } from 'components/lmByggnadPopup/LmPopupRow';
import {
  clearFeaturesInTemplateSources,
  clearSelectedLmByggnadLayers,
  popupOverlay,
  templatePointLayer,
  templatePolygonLayer
} from 'components/map/Layers';

import { BebyggelseobjektNyttEnskiltPost, BebyggelseobjektNyttOmradePost, Huvudgrupp } from 'models';

import { BebyggelseobjektgruppEnum } from 'enums';

import { GeoJSONPolygon } from 'ol/format/GeoJSON';
import { Geometry, Polygon } from 'ol/geom';

import { LMByggnadSlice, useSearchLmByggnadMutation } from 'api/endpoints/LMByggnadSlice';
import classNames from 'classnames';
import { FeatureCollection } from 'geojson';

const LmByggnadPopup: FC = (): ReactElement => {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);

  const [, results] = useSearchLmByggnadMutation({ fixedCacheKey: 'lm-byggnad' });
  const { data: lmByggnader, isLoading: lmByggnaderIsLoading } = results;

  const { data: aktivitetData } = useAppSelector((state: RootState) => state.aktivitet);
  const aktivitet = useGetCurrentAktivitet(aktivitetData);

  const { requestStatus: utkastRequestStatus, requestType } = useAppSelector((state: RootState) => state.utkast);

  if (ref.current) {
    popupOverlay.setElement(ref.current);
  }

  const showPopup = useCallback(() => {
    popupOverlay.setPosition(lmByggnader?.requestedAtCoordinate);
  }, [lmByggnader?.requestedAtCoordinate]);

  const closePopup = useCallback(() => {
    popupOverlay.setPosition(undefined);
    clearFeaturesInTemplateSources();
    clearSelectedLmByggnadLayers();
  }, []);

  useEffect(() => {
    if (lmByggnader?.requestedAtCoordinate) {
      showPopup();
    } else {
      closePopup();
    }
  }, [closePopup, lmByggnader?.requestedAtCoordinate, showPopup]);

  const handleTemplateButtonHover = (active: boolean) => {
    dispatch(setHoveredId(active ? 'template' : ''));
    templatePointLayer.changed();
    templatePolygonLayer.changed();
  };

  const handleOmradeTemplateButtonHover = (active: boolean) => {
    dispatch(setHoveredId(active ? 'omradeTemplate' : ''));
    templatePointLayer.changed();
    templatePolygonLayer.changed();
  };

  const createFristaendeUtkast = async (
    bebyggelseobjektgrupp: Huvudgrupp.HuvudgrupptypEnum,
    geometri: Geometry,
    aktivitetId: string
  ) => {
    const polygon = geometri as Polygon;
    const featureCollection: FeatureCollection<GeoJSONPolygon> = {
      type: 'FeatureCollection',
      crs: {
        type: 'name',
        properties: { name: 'EPSG:3006' }
      },
      features: [
        {
          properties: {},
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: polygon.getCoordinates() }
        }
      ]
    };

    let bebyggelseobjektPost: BebyggelseobjektNyttOmradePost | BebyggelseobjektNyttEnskiltPost = {
      aktivitet_id: aktivitetId,
      geometri: featureCollection,
      bebyggelseobjektgrupp: BebyggelseobjektgruppEnum.OMRADE
    };

    if (bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.ENSKILT_BEBYGGELSEOBJEKT) {
      bebyggelseobjektPost = {
        ...bebyggelseobjektPost,
        bebyggelseobjektgrupp: BebyggelseobjektgruppEnum.ENSKILT_BEBYGGELSEOBJEKT,
        utkasttyp: 'FRISTAENDE'
      } as BebyggelseobjektNyttEnskiltPost;
    }

    try {
      const results = await dispatch(postUtkast(bebyggelseobjektPost)).unwrap();
      dispatch(addBebyggelseobjektUtkast([results]));
      dispatch(clearUtkastState());
    } catch (error: any) {
      return Promise.reject(error);
    }
    dispatch(LMByggnadSlice.util.resetApiState());
    return Promise.resolve();
  };

  const createLmUtkast = async (lmRegisterbyggnadId: string, aktivitetId: string) => {
    try {
      const bebyggelseobjektNyPost: BebyggelseobjektNyttEnskiltPost = {
        utkasttyp: 'BEFINTLIGT',
        bebyggelseobjektgrupp: BebyggelseobjektgruppEnum.ENSKILT_BEBYGGELSEOBJEKT,
        aktivitet_id: aktivitetId,
        lm_registerbyggnad_id: lmRegisterbyggnadId
      };
      const results = await dispatch(postUtkast(bebyggelseobjektNyPost)).unwrap();
      dispatch(addBebyggelseobjektUtkast([results]));
      dispatch(clearUtkastState());
    } catch (error: any) {
      return Promise.reject(error);
    }
    dispatch(LMByggnadSlice.util.resetApiState());
    return Promise.resolve();
  };

  const isPending = (requestType === 'post' || requestType === 'patch') && utkastRequestStatus === 'pending';

  return (
    <div
      ref={ref}
      className={classNames(
        'ol-popup tw-left-[-16em] tw-flex tw-max-h-[30rem] tw-min-w-[320px] tw-flex-col tw-rounded-md tw-shadow-md before:tw-left-[16em] after:tw-left-[16em] md:tw-left-[-26em] md:tw-min-w-[29rem] before:md:tw-left-[26em] after:md:tw-left-[26em]',
        { 'tw-hidden': !lmByggnader?.requestedAtCoordinate || lmByggnaderIsLoading }
      )}
    >
      <button
        className="ol-popup-closer tw-text-size-1.313 tw-text-raa-orange"
        onClick={() => dispatch(LMByggnadSlice.util.resetApiState())}
        aria-label="stäng popup"
      />

      <div className="tw-h-8 tw-border-b tw-border-raa-gray-3 tw-p-2">
        <p className="tw-justify-center tw-font-semibold">Lägg till och skapa utkast:</p>
      </div>

      {lmByggnader && lmByggnader.byggnadIndex.length > 0 ? (
        <ul>
          {lmByggnader.byggnadIndex.map((byggnad) => (
            <li className="tw-w-full" key={byggnad.lmbyggnad.objektidentitet}>
              <LmPopupRow
                byggnad={byggnad}
                requestType={requestType}
                utkastRequestStatus={utkastRequestStatus}
                createUtkast={createLmUtkast}
                aktivitetId={aktivitet?.aktivitet_id || ''}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className="tw-flex tw-gap-4 tw-bg-raa-gray-2 tw-p-4">
          <NewObjectButton
            text="Nytt objekt"
            disabled={isPending}
            isPending={isPending}
            onHover={handleTemplateButtonHover}
            onClick={() => {
              const geometry = templatePolygonLayer.getSource()?.getFeatures()[0]?.getGeometry();
              geometry &&
                createFristaendeUtkast(
                  BebyggelseobjektgruppEnum.ENSKILT_BEBYGGELSEOBJEKT,
                  geometry,
                  aktivitet?.aktivitet_id || ''
                );
            }}
          />
          <NewObjectButton
            text="Nytt område"
            disabled={isPending}
            isPending={isPending}
            onHover={handleOmradeTemplateButtonHover}
            onClick={() => {
              const geometry = templatePolygonLayer.getSource()?.getFeatures()[1]?.getGeometry();
              geometry &&
                createFristaendeUtkast(BebyggelseobjektgruppEnum.OMRADE, geometry, aktivitet?.aktivitet_id || '');
            }}
          />
        </div>
      )}
    </div>
  );
};

const NewObjectButton: FC<{
  onClick: () => void;
  onHover: (hovered: boolean) => void;
  text: string;
  isPending: boolean;
  disabled: boolean;
}> = ({ onClick, onHover, text, isPending, disabled }) => {
  return (
    <button
      type="button"
      className="raa-button tw-flex-1"
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onClick={onClick}
      disabled={disabled}
    >
      {isPending ? <RaaSpinner size={'1rem'} /> : <span>{text}</span>}
    </button>
  );
};

export default LmByggnadPopup;
